import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { ApiExError } from '@shared';

const initialConsentState: ConsentState = {
  applicationConsentStatus: {
    data: null,
    isLoading: false,
    errorMessage: null,
  },
  postConsentRecord: {
    data: null,
    isLoading: false,
    errorMessage: null,
  },
  sendConsentNotificationEmail: {
    data: null,
    isLoading: false,
    errorMessage: null,
  },
  postRecordCarePacketDownload: {
    data: null,
    isLoading: false,
    errorMessage: null,
  },
};

const consentSlice = createSlice({
  name: 'consent',
  initialState: initialConsentState,
  reducers: {
    // Fetch Application Consent Status Actions
    fetchApplicationConsentStatusByClientPolicyApplicationIdRequest(
      state,
      action: PayloadAction<{
        clientPolicyApplicationId: string;
        queryParams?: Record<string, string>;
      }>,
    ) {
      state.applicationConsentStatus.isLoading = true;
      state.applicationConsentStatus.errorMessage = null;
    },
    fetchApplicationConsentStatusByClientPolicyApplicationIdResponse(
      state,
      action: PayloadAction<ApplicationConsentStatusResponse>,
    ) {
      state.applicationConsentStatus.isLoading = false;
      state.applicationConsentStatus.data = action.payload;
      state.applicationConsentStatus.errorMessage = null;
    },
    fetchApplicationConsentStatusByClientPolicyApplicationIdFailure(
      state,
      action: PayloadAction<ApiExError>,
    ) {
      state.applicationConsentStatus.isLoading = false;
      state.applicationConsentStatus.errorMessage = action.payload.message;
    },

    // Post Consent Record Actions
    postConsentRecordRequest(state, action: PayloadAction<PostConsentRecordBodyProps>) {
      state.postConsentRecord.isLoading = true;
      state.postConsentRecord.errorMessage = null;
    },
    postConsentRecordResponse(state, action: PayloadAction<PostConsentRecordResponse>) {
      state.postConsentRecord.isLoading = false;
      state.postConsentRecord.data = action.payload;
      state.postConsentRecord.errorMessage = null;
    },
    postConsentRecordFailure(state, action: PayloadAction<ApiExError>) {
      state.postConsentRecord.isLoading = false;
      state.postConsentRecord.errorMessage = action.payload.message;
    },

    // Send Consent Notification Email Actions
    postSendConsentNotificationEmailRequest(
      state,
      action: PayloadAction<PostSendConsentNotificationEmailBodyProps>,
    ) {
      state.sendConsentNotificationEmail.isLoading = true;
      state.sendConsentNotificationEmail.errorMessage = null;
    },
    postSendConsentNotificationEmailResponse(
      state,
      action: PayloadAction<SendConsentNotificationEmailResponse>,
    ) {
      state.sendConsentNotificationEmail.isLoading = false;
      state.sendConsentNotificationEmail.data = action.payload;
      state.sendConsentNotificationEmail.errorMessage = null;
    },
    postSendConsentNotificationEmailFailure(state, action: PayloadAction<ApiExError>) {
      state.sendConsentNotificationEmail.isLoading = false;
      state.sendConsentNotificationEmail.errorMessage = action.payload.message;
    },

    // Post Record Care Packet Download Actions
    postRecordCarePacketDownloadRequest(
      state,
      action: PayloadAction<PostRecordCarePacketDownloadBodyProps>,
    ) {
      state.postRecordCarePacketDownload.isLoading = true;
      state.postRecordCarePacketDownload.errorMessage = null;
    },
    postRecordCarePacketDownloadResponse(
      state,
      action: PayloadAction<PostRecordCarePacketDownloadResponse>,
    ) {
      state.postRecordCarePacketDownload.isLoading = false;
      state.postRecordCarePacketDownload.data = action.payload;
      state.postRecordCarePacketDownload.errorMessage = null;
    },
    postRecordCarePacketDownloadFailure(state, action: PayloadAction<ApiExError>) {
      state.postRecordCarePacketDownload.isLoading = false;
      state.postRecordCarePacketDownload.errorMessage = action.payload.message;
    },

    // Clear actions
    clearApplicationConsentStatus(state) {
      state.applicationConsentStatus = {
        data: null,
        isLoading: false,
        errorMessage: null,
      };
    },
    clearPostConsentRecord(state) {
      state.postConsentRecord = {
        data: null,
        isLoading: false,
        errorMessage: null,
      };
    },
    clearSendConsentNotificationEmail(state) {
      state.sendConsentNotificationEmail = {
        data: null,
        isLoading: false,
        errorMessage: null,
      };
    },
    clearPostRecordCarePacketDownload(state) {
      state.postRecordCarePacketDownload = {
        data: null,
        isLoading: false,
        errorMessage: null,
      };
    },
  },
});

export const {
  fetchApplicationConsentStatusByClientPolicyApplicationIdRequest,
  fetchApplicationConsentStatusByClientPolicyApplicationIdResponse,
  fetchApplicationConsentStatusByClientPolicyApplicationIdFailure,
  postConsentRecordRequest,
  postConsentRecordResponse,
  postConsentRecordFailure,
  postSendConsentNotificationEmailRequest,
  postSendConsentNotificationEmailResponse,
  postSendConsentNotificationEmailFailure,
  postRecordCarePacketDownloadRequest,
  postRecordCarePacketDownloadResponse,
  postRecordCarePacketDownloadFailure,
  clearApplicationConsentStatus,
  clearPostConsentRecord,
  clearSendConsentNotificationEmail,
  clearPostRecordCarePacketDownload,
} = consentSlice.actions;

export const consentReducer = consentSlice.reducer;
